import { Box, Divider } from '@chakra-ui/react';
import { ListItemText, NavGroupL2, NavGroupTitleL2, NavItemL2 } from '@mesh.ai/rabbit';
import { L3_PATHS, staticRoutes } from 'Deer/constants/routes';
import { useDeerViewId } from 'Deer/hooks/useDeerViewId';
import { useRouteToMyTeamLevels } from 'Deer/hooks/useRouteToL3';
import { useViewFromQueryParam } from 'Deer/hooks/useViewFromQueryParam';
import { EnabledFeatures } from 'Deer/types/logeedInUsersAccesses';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { NavLink, useHistory } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import { isNetchexLogin } from 'constants/netChexConstants';

import { L2_MODULES_TEAM } from '../../constants';
import { PeopleList } from './People/PeopleList';

const activeClassName = 'active-L2';

export const getApplicableL2MyTeamModules = (
  teamType: 'directReports' | 'allReports' | 'view',
  customGoalName: string
) => {
  if (!teamType) return [];
  const L2_MODULES_WITH_CUSTOM_GOAL_NAME = L2_MODULES_TEAM[teamType];

  L2_MODULES_WITH_CUSTOM_GOAL_NAME.goals.label = modifyString(customGoalName, StringModifier.PLURAL_TITLE_CASE);

  L2_MODULES_WITH_CUSTOM_GOAL_NAME.review.value = checkIntentLandingPagesFeatureEnabled()
    ? L3_PATHS.team[teamType].review.completionStatus
    : L3_PATHS.team[teamType].review.manage;

  return Object.values(L2_MODULES_WITH_CUSTOM_GOAL_NAME)
    .filter((module) => module.checkModuleIsEnabled())
    .sort((a, b) => a.order - b.order);
};

export const getApplicableL2ViewModules = (moduleName, enabledFeatures: Array<EnabledFeatures>) => {
  const L2_MODULES_WITH_CUSTOM_GOAL_NAME = L2_MODULES_TEAM.view;

  L2_MODULES_WITH_CUSTOM_GOAL_NAME.goals.label = modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE);

  L2_MODULES_WITH_CUSTOM_GOAL_NAME.review.value = checkIntentLandingPagesFeatureEnabled()
    ? L3_PATHS.team.view.review.completionStatus
    : L3_PATHS.team.view.review.manage;

  return (
    Object.values(L2_MODULES_WITH_CUSTOM_GOAL_NAME)
      // @ts-expect-error fix feature key type
      .filter((module) => module.checkModuleIsEnabled() && enabledFeatures?.includes(module.featureKey))
      .sort((a, b) => a.order - b.order)
  );
};

export const getViewRoute = (route: string, viewId: string) => {
  const existingSearchParams = route?.split('?')[1];
  return existingSearchParams ? `${route}&viewId=${viewId}` : `${route}?viewId=${viewId}`;
};

export const MyTeamModules = () => {
  const history = useHistory();
  const view = useSelectedView();
  const { moduleName } = useOrganisationSettings();
  const { l2Module, teamType } = useRouteToMyTeamLevels();

  const { viewId } = useDeerViewId();

  const isViewPage = teamType === 'view';

  const { data: viewFromQueryParam, isError } = useViewFromQueryParam();

  const viewToUse = isViewPage ? viewFromQueryParam : view;

  // Here the new API should be integrated which will return us the views
  // config for the viewId. So we can confirm what features are enabled
  const applicableL2Modules = isViewPage
    ? getApplicableL2ViewModules(moduleName, viewToUse?.enabledFeatures)
    : getApplicableL2MyTeamModules(teamType, moduleName);

  if (!teamType) return null;

  if (isViewPage && isError && !isNetchexLogin) {
    history.replace(staticRoutes.home);
  }

  return (
    <>
      <Box>
        <NavGroupL2>
          <NavGroupTitleL2>PORTFOLIO</NavGroupTitleL2>

          {applicableL2Modules.map((module) => (
            <NavLink
              key={module.value}
              to={isViewPage ? getViewRoute(module.value, viewId) : module.value}
              activeClassName={activeClassName}
              isActive={() => l2Module === module.key}
            >
              <NavItemL2>
                <ListItemText title={module.label} />
              </NavItemL2>
            </NavLink>
          ))}
        </NavGroupL2>
      </Box>
      <Divider color="#EBEFF3" opacity={1} my="12px" />
      <Box>
        <PeopleList />
      </Box>
    </>
  );
};
